/**
 * @module app.track.filterService
 */

import {TRACK_FILTER_ID} from "../constants";

class TrackFilterService {
  /**
   * @param {angular.$http} $http Angular Http service.
   * @param {angularGettext.Catalog} gettextCatalog
   * @param {string} apiUrl API url prefix.
   * @param {angular.$q} $q Angular Q service.
   * @param {app.track.service} appTrackService
   * @param {ngeo.statemanager.Location} ngeoLocation Location service from NGEO.

   * @ngInject
   */
  constructor($http, gettextCatalog, apiUrl, $q, appTrackService, ngeoLocation) {

    /**
     * @private
     */
    this.http_ = $http;

    this.gettextCatalog = gettextCatalog;

    /**
     * @private
     */
    this.apiUrl_ = apiUrl;

    /**
     * @private
     */
    this.q_ = $q;

    /**
     * @private
     */
    this.trackService_ = appTrackService;

    /**
     * @type {appx.TrackFilter}
     * @export
     */
    this.trackListFilter = {
      id: TRACK_FILTER_ID.ALL,
      name: 'all tours',
    };

    /**
     * @type {Array.<appx.TrackFilter>}
     * @export
     */
    this.filtersList;

    /**
     * @type {Array.<appx.TrackFilter>}
     * @export
     */
    this.filtersListPending = [];

    /**
     * @type {string}
     */
    this.defaultName = this.gettextCatalog.getString('undefined filter');

    /**
     * @type {appx.TrackFilter}
     * @export
     */
    this.selectedFilter = this.getDefaultFilter();

    /**
     * @type {Array.<function()>}
     * @private
     */
    this.filtersPromises_ = [];

    /**
     * @type {boolean}
     */
    this.isDirty = false;

  }

  init() {
    if (this.trackService_.ownSelectedTrack()) {
      if (!this.filtersList) {
        // update filter list then select filter from track
        this.updateList().then(() => {
          this.selectFilterFromTrack();
        });
      } else {
        if (this.trackService_.selectedTrack != undefined) {
          this.selectFilterFromTrack();
        }
      }
    }
  }

  updateList() {
    return this.http_.get(this.apiUrl_ + '4/filters').then((response) => {
      this.filtersList = response.data.sort(this.alphanumericSort);
      // make an independant copy to be able to defer actions on filters list
      this.filtersListPending = this.filtersList.map(item=>Object.assign({}, item));
      this.isDirty = false;
    }).catch((response) => {
      bootbox.alert({
        message: this.gettextCatalog.getString('An error occurred when trying to save the filters')
      });
    });
  }

  setFilterToTrack() {
    if (this.selectedFilter.id !== TRACK_FILTER_ID.OTHER) {
      this.trackService_.selectedTrack.properties['filterid'] = this.selectedFilter.id;
    } else {
      this.trackService_.selectedTrack.properties['filterid'] = null;
    }
  }

  selectFilterFromTrack() {
    // get filter id from track if set, otherwise use default filter
    this.selectedFilter = this.filtersListPending.find(f => f.id === this.getFilterIdFromTrack_()) || this.getDefaultFilter();
  }

  /**
   * @private
   * @return {number}
   */
  getFilterIdFromTrack_() {
    if (this.trackService_.selectedTrack !== undefined) {
      const filterId = this.trackService_.selectedTrack.properties['filterid'];
      return filterId;
    } else {
      return -1;
    }
  }

  /**
   * @return {appx.TrackFilter}
   */
  getDefaultFilter() {
    return {'id': -1, 'name': this.defaultName};
  }

  /**
   * @param {appx.TrackFilter} newFilter
   */
  addFilterAndSort(newFilter) {
    this.filtersListPending.push(newFilter);
    this.filtersListPending.sort(this.alphanumericSort);
    this.isDirty = true;
  }

  /**
   * @return {Promise|!angular.$q.Promise}
   */
  save() {
    // handle promises for new filters
    this.prepareNewFiltersSave_();

    // handle promises for deleted filters > directly taken care of in the deleteFilter method

    // handle promises for modified filters
    this.prepareModifiedFiltersSave_();

    // apply all changes if any
    if (this.isDirty) {

      const promises = this.filtersPromises_.map(fn => fn());
      const promise = Promise.all(promises).then(() => this.updateList()).then(() => {
        if (this.selectedFilter.id < -1) {
          this.selectedFilter = this.filtersListPending.find(f => f.name === this.selectedFilter.name) || this.getDefaultFilter();
          this.setFilterToTrack();
        }
        this.resetPromises_();
      }).catch((response) => {
        bootbox.alert({
          message: this.gettextCatalog.getString('An error occurred when trying to save the filters')
        });
      });
      return promise;

    } else {
      this.resetPromises_();
      return this.q_.resolve();
    }
  }

  /**
  * Update track filter directly
  * @export
  */
  saveDirect() {
    let data = {
      'property': 'filterid',
      'value': this.selectedFilter.id
    };
    let url = this.apiUrl_ + '4/tracks/' + this.trackService_.selectedTrack.id + '/update_property';
    if (this.trackService_.selectedTrack.recorded) {
      url = this.apiUrl_ + '5/recordedtracks/' + this.trackService_.selectedTrack.id;
      data = {filter_id: this.trackService_.selectedTrack.properties.filterid};
    }
    this.http_.put(url, data).catch((error) => {
      bootbox.alert({
        message: this.gettextCatalog.getString('An error occurred when trying to update the filter of this track.')
      });
    });
  }

  /**
   * @private
   */
  prepareNewFiltersSave_() {
    this.filtersListPending.forEach((filter, key) => {
      if (filter.id !== -1) {
        const existingFilter = this.filtersList.find(f => f.id === filter.id);
        if (!existingFilter) {
          const promise = () => {
            return this.http_.post(this.apiUrl_ + '4/filters', filter).catch((response) => {
              bootbox.alert({
                message: this.gettextCatalog.getString('An error occurred when trying to save this filter')
              });
            });
          };
          this.filtersPromises_.push(promise);
        }
      }
    });
  }

  /**
   * @private
   */
  prepareModifiedFiltersSave_() {
    this.filtersListPending.forEach((filter, key) => {
      if (filter.id < 0) {
        // -1 is "other", -2 and less are temporary new filters before being saved
        return;
      }
      const backendFilter = this.filtersList.find(f => f.id === filter.id);
      // dont compare new filters name, only already existing filters
      if (backendFilter.id >= 0 && filter.name !== backendFilter.name) {

        this.isDirty = true;

        const callPromise = () => {
          return this.http_.put(this.apiUrl_ + '4/filters/' + filter.id, filter).catch((response) => {
            bootbox.alert({
              message: this.gettextCatalog.getString('An error occurred when trying to update this filter')
            });
          });
        };
        this.filtersPromises_.push(callPromise);
      }
    });
  }

  /**
   * @param {number} filterId
   */
  deleteFilter(filterId) {
    if (filterId === -1) {
      return; // FIXME: we should never be asked to remove the -1 filter
    }

    const actuallyRemoveFilterFromTheList = () => {
      const index = this.filtersListPending.findIndex(item => item.id === filterId);
      console.assert(index !== -1);
      this.filtersListPending.splice(index, 1);
      if (this.getFilterIdFromTrack_() && this.getFilterIdFromTrack_() == filterId) {
        // we deleted the filter from the currently selected track,
        // but we cant reload it from here, so we just set the current track filter to the default -1
        this.selectedFilter = this.getDefaultFilter();
        this.setFilterToTrack();
      }
    };

    if (filterId < 0) {
      // Directly remove local filter
      actuallyRemoveFilterFromTheList();
      return;
    }

    // Delete existing filter from server
    bootbox.addLocale('delete_filter', {
      OK: this.gettextCatalog.getString('OK'),
      CANCEL: this.gettextCatalog.getString('Cancel deletion'),
      CONFIRM: this.gettextCatalog.getString('Delete')
    });
    bootbox.setLocale('delete_filter');

    bootbox.confirm({
      title: this.gettextCatalog.getString('Delete Filter'),
      message: this.gettextCatalog.getString('Are you sure you want to delete the filter?'),
      callback: (okToDelete) => {
        if (okToDelete) {

          this.isDirty = true;

          this.filtersPromises_.push(() => {
            return this.http_.delete(this.apiUrl_ + '4/filters/' + filterId).catch(() => {
              bootbox.alert({
                message: this.gettextCatalog.getString('An error occurred when trying to delete this filter')
              });
            });
          });
          actuallyRemoveFilterFromTheList();
        }
      }
    });
  }

  /**
   * @private
   */
  resetPromises_() {
    this.filtersPromises_ = [];
  }

  cleanUp() {
    this.updateList();
    this.resetPromises_();
  }

  /**
   * Sort alphanumerically TrackFilter objects by name
   * @param {appx.TrackFilter} a
   * @param {appx.TrackFilter} b
   * @return {number}
   */
  alphanumericSort(a, b) {
    const aname = a.name || '';
    const bname = b.name || '';
    if (aname.toLowerCase() === bname.toLowerCase()) {
      return 0;
    }
    if (typeof a === typeof b) {
      return aname.toLowerCase() < bname.toLowerCase() ? -1 : 1;
    }
    return typeof a < typeof b ? -1 : 1;
  }
}

export default angular.module('appTrackFilterService', []).service('appTrackFilterService', TrackFilterService);

